import React, { ChangeEvent, CSSProperties, FC, ReactNode, useEffect, useRef } from 'react'
import TsxUtils from 'utils/tsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faMinus } from '@fortawesome/free-solid-svg-icons'
import './Checkbox.scss'
import { CypressSelectable } from '../../../types'

interface CheckboxProps extends CypressSelectable {
  id?: string
  className?: string
  style?: CSSProperties
  checked: boolean
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  children?: ReactNode
  readOnly?: boolean
  disabled?: boolean
  // a part of what the checkbox stands for is picked, it is neither checked nor unchecked
  indeterminate?: boolean
}

const Checkbox: FC<CheckboxProps> = ({
  id,
  className,
  style,
  onChange,
  checked,
  children,
  readOnly,
  disabled,
  indeterminate,
  dataCy,
}) => {
  const input = useRef<HTMLInputElement>(null)

  // checked wins over indeterminate, the same way the dom flag does
  const partial = !!indeterminate && !checked

  // the flag is no attribute, it can only be assigned on the element itself
  useEffect(() => {
    if (input.current) {
      input.current.indeterminate = partial
    }
  }, [partial])

  return (
    <div
      className={'Checkbox' + TsxUtils.extraStyle(className, className)}
      style={style}
    >
      <label
        htmlFor={id}
        className={
          'Checkbox__label' +
          TsxUtils.extraStyle(!readOnly && !disabled, 'Checkbox__label--editable')
        }
      >
        <input
          ref={input}
          className='Checkbox__input'
          id={id}
          type='checkbox'
          checked={checked}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (!readOnly && !disabled && onChange) {
              onChange(e)
            }
          }}
          readOnly={!onChange || readOnly}
          disabled={disabled}
        />

        <span
          data-cy={dataCy}
          className={
            `Checkbox__icon-container` +
            TsxUtils.extraStyle(checked || partial, 'Checkbox__icon-container--checked') +
            TsxUtils.extraStyle(readOnly, 'Checkbox__icon-container--read-only') +
            TsxUtils.extraStyle(disabled, 'Checkbox__icon-container--disabled')
          }
        >
          {(checked || partial) && (
            <FontAwesomeIcon
              icon={partial ? faMinus : faCheck}
              className={
                'Checkbox__icon' +
                TsxUtils.extraStyle(readOnly, 'Checkbox__icon--read-only') +
                TsxUtils.extraStyle(disabled, 'Checkbox__icon--disabled')
              }
            />
          )}
        </span>

        <span
          className={
            'Checkbox__title' +
            TsxUtils.extraStyle(readOnly, 'Checkbox__title--read-only') +
            TsxUtils.extraStyle(disabled, 'Checkbox__title--disabled')
          }
        >
          {children}
        </span>
      </label>
    </div>
  )
}

export default Checkbox
